import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { FormGroup, Label, Input, FormFeedback, Row, Col, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { ArrowLeft, Plus } from 'react-feather';
import { useDispatch } from 'react-redux';
import { checkBrandDataExist } from '../../../ReduxToolkit/Reducers/brands';
import BrandInSpaceRow from '../BrandInSpaceRow';

const Step5PutBrandRetailers = ({ control, errors, onBackToSelection }) => {
  const { setValue, watch } = useFormContext();
  const dispatch = useDispatch();
  const [brandBApiMessages, setBrandBApiMessages] = useState({});
  const brand_in_space = watch('brand_in_space');
  const brandInfoName = watch('brand_info.brand_name');
  const brandInfoWebsite = watch('brand_info.website');
  const brandInfoId = watch('brand_info.brand_id');

  useEffect(() => {
    setValue('find_matches.brand_a.brand_id', brandInfoId ?? '', {
      shouldValidate: true,
      shouldDirty: false,
    });
    setValue('find_matches.brand_a.name', brandInfoName ?? '', {
      shouldValidate: true,
      shouldDirty: false,
    });
    setValue('find_matches.brand_a.website', brandInfoWebsite ?? '', {
      shouldValidate: true,
      shouldDirty: false,
    });

    setValue('find_matches.brand_b', brand_in_space?.brands || [], {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [brandInfoName, brandInfoWebsite, brandInfoId, setValue]);

  const handleBrandBBlur = async (index) => {
    const values = watch('find_matches.brand_b');
    const { name, website } = values?.[index] || {};
    if (!name || !website) return;

    setBrandBApiMessages(prev => ({ ...prev, [index]: { loading: true, message: null } }));
    const result = await dispatch(checkBrandDataExist({ brand_name: name, brand_website: website, type: 'matches' }));
    if (result) {
      setBrandBApiMessages(prev => ({ ...prev, [index]: { loading: false, message: result.message, statusCode: result.statusCode } }));
    }
  };

  const { fields: brandBFields, append, remove } = useFieldArray({
    control,
    name: 'find_matches.brand_b',
  });

  const addBrand = () => {
    append({ brand_id: '', name: '', website: '' });
  };

  const removeBrand = (index) => {
    remove(index);
    setBrandBApiMessages(prev => {
      const updated = {};
      Object.keys(prev).forEach(key => {
        const k = Number(key);
        if (k < index) updated[k] = prev[key];
        else if (k > index) updated[k - 1] = prev[key];
      });
      return updated;
    });
  };

  const renderBrandBWebsiteFooter = (index) => (
    <>
      {brandBApiMessages[index]?.loading && (
        <div style={apiMessageLoadingStyle}>Checking...</div>
      )}
      {!brandBApiMessages[index]?.loading && brandBApiMessages[index]?.message && (
        <div style={brandBApiMessages[index]?.statusCode === 200 ? apiMessageSuccessStyle : apiMessageErrorStyle}>
          {brandBApiMessages[index].message}
        </div>
      )}
    </>
  );

  return (
    <div style={{
      maxWidth: '808px',
      width: '100%',
      background: '#fff',
      borderRadius: '10px',
      padding: '32px 36px',
    }}>
      <h5 style={{ fontWeight: '600', fontSize: '18px', marginBottom: '4px' }}>
        Put Brand B Retailers in Brand A
      </h5>
      <p style={{ color: '#888', fontSize: '13px', marginBottom: '28px' }}>
        Use Brand A as target space and inject retailers from Brand B. Optionally use a brand ID to auto-fill Brand B name and website.
      </p>

      <div style={{ marginBottom: '32px' }}>
        <h6 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
          Brand A (Target Brand) <span style={{ color: '#e53' }}>*</span>
        </h6>
        <p style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>
          Populated from Brand Info in step 1
        </p>

        <Row style={{ marginBottom: '16px' }}>
          <Col md={4}>
            <FormGroup>
              <Label style={labelStyle}>Brand ID</Label>
              <Controller
                name="find_matches.brand_a.brand_id"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    disabled
                    placeholder="From step 1"
                    style={disabledInputStyle}
                  />
                )}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label style={labelStyle}>Name*</Label>
              <Controller
                name="find_matches.brand_a.name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Luxewear"
                    disabled
                    invalid={!!errors?.find_matches?.brand_a?.name}
                    style={disabledInputStyle}
                  />
                )}
              />
              {errors?.find_matches?.brand_a?.name && (
                <FormFeedback style={{ display: 'block' }}>
                  {errors.find_matches.brand_a.name.message}
                </FormFeedback>
              )}
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label style={labelStyle}>Website URL*</Label>
              <Controller
                name="find_matches.brand_a.website"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="http://Luxewear.com"
                    disabled
                    invalid={!!errors?.find_matches?.brand_a?.website}
                    style={disabledInputStyle}
                  />
                )}
              />
              {errors?.find_matches?.brand_a?.website && (
                <FormFeedback style={{ display: 'block' }}>
                  {errors.find_matches.brand_a.website.message}
                </FormFeedback>
              )}
            </FormGroup>
          </Col>
        </Row>
      </div>

      <div style={{ marginBottom: '32px' }}>
        <h6 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
          Brand B (Source Brand) <span style={{ color: '#e53' }}>*</span>
        </h6>

        {brandBFields.map((field, index) => (
          <BrandInSpaceRow
            key={field.id}
            control={control}
            errors={errors}
            index={index}
            onRemove={() => removeBrand(index)}
            canRemove={brandBFields.length > 1}
            fieldPrefix="find_matches.brand_b"
            nameLabel="Name"
            namePlaceholder="Luxewear"
            rowMarginBottom="16px"
            onWebsiteBlur={handleBrandBBlur}
            websiteFooter={renderBrandBWebsiteFooter(index)}
          />
        ))}

        <button type="button" onClick={addBrand} style={addBtnStyle}>
          <Plus size={16} />
          Add another
        </button>
      </div>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label style={labelStyle}>
              Match Type <span style={{ color: '#e53' }}>*</span>
            </Label>
            <Controller
              name="find_matches.match_type"
              control={control}
              render={({ field: { value, onChange } }) => (
                <UncontrolledDropdown>
                  <DropdownToggle caret color="white" size="sm" style={{ textTransform: 'capitalize', minWidth: '100%' }}>
                    {value}
                  </DropdownToggle>
                  <DropdownMenu style={{ textTransform: 'capitalize', minWidth: '100%' }}>
                    <DropdownItem onClick={() => onChange('match')}>
                      Match
                    </DropdownItem>
                    <DropdownItem onClick={() => onChange('copy')}>
                      Copy
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            />
            {errors?.find_matches?.match_type && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {errors.find_matches.match_type.message}
              </div>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label style={labelStyle}>
              Previous Matching <span style={{ color: '#e53' }}>*</span>
            </Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px' }}>
              <Controller
                name="find_matches.remove_previous_matching"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={value || false}
                      onChange={(e) => onChange(e.target.checked)}
                      style={{ marginRight: '8px', width: '16px', height: '16px' }}
                    />
                    Remove Previous matching
                  </label>
                )}
              />
            </div>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <FormGroup>
            <Label style={labelStyle}>
              Next To Brands &amp; Competitors
            </Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px' }}>
              <Controller
                name="brand_info.remove_existing_next_to_brands_competitors"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={value || false}
                      onChange={(e) => onChange(e.target.checked)}
                      style={{ marginRight: '8px', width: '16px', height: '16px' }}
                    />
                    Removing existing next to brands &amp; competitors
                  </label>
                )}
              />
            </div>
          </FormGroup>
        </Col>
      </Row>

      <div style={{ marginTop: '24px' }}>
        <button
          type="button"
          onClick={onBackToSelection}
          style={backLinkStyle}
        >
          <ArrowLeft size={14} />
          Back to selection
        </button>
      </div>
    </div>
  );
};

const labelStyle = {
  fontSize: '13px',
  fontWeight: '500',
  color: '#333',
  marginBottom: '6px',
};

const disabledInputStyle = {
  fontSize: '13px',
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '10px 12px',
  backgroundColor: '#f5f5f5',
  cursor: 'not-allowed',
  color: '#666',
};

const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'none',
  border: 'none',
  color: '#666',
  fontSize: '13px',
  cursor: 'pointer',
  padding: 0,
};

const addBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'none',
  border: 'none',
  color: '#222',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
  padding: '6px 0',
};

const checkboxLabelStyle = {
  fontSize: '13px',
  color: '#333',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  margin: 0,
};

const apiMessageLoadingStyle = {
  fontSize: '12px',
  color: '#888',
  marginTop: '4px',
};

const apiMessageSuccessStyle = {
  fontSize: '12px',
  color: '#28a745',
  marginTop: '4px',
};

const apiMessageErrorStyle = {
  fontSize: '12px',
  color: '#dc3545',
  marginTop: '4px',
};

export default Step5PutBrandRetailers;